import React, { useEffect, useRef } from "react";

// Shiny gold "digital rain" that flows UPWARD, rendered on a fixed
// full-screen canvas behind all page content.
export default function DigitalRain() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const chars =
      "アカサタナハマヤラワ0123456789ABCDEFｱｲｳｴｵ$¥€FFA01".split("");
    const fontSize = 16;
    let columns = 0;
    let drops = [];
    let speeds = [];
    let animationId;

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.floor(window.innerWidth / fontSize);
      drops = new Array(columns)
        .fill(0)
        .map(() => Math.random() * (window.innerHeight / fontSize));
      speeds = new Array(columns).fill(0.069);
    };

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      // translucent dark overlay to create fading trails
      ctx.fillStyle = "rgba(8, 8, 10, 0.10)";
      ctx.fillRect(0, 0, w, h);

      ctx.font = `${fontSize}px 'JetBrains Mono', monospace`;
      ctx.textAlign = "center";

      for (let i = 0; i < columns; i++) {
        const x = i * fontSize + fontSize / 2;
        const y = drops[i] * fontSize;
        const char = chars[Math.floor(Math.random() * chars.length)];

        // leading (head) character is bright & shiny
        ctx.shadowColor = "rgba(232, 199, 102, 0.9)";
        ctx.shadowBlur = 12;
        ctx.fillStyle = "rgba(248, 234, 178, 0.95)";
        ctx.fillText(char, x, y);

        // trailing character, dimmer gold
        ctx.shadowBlur = 0;
        ctx.fillStyle = "rgba(200, 169, 81, 0.45)";
        ctx.fillText(
          chars[Math.floor(Math.random() * chars.length)],
          x,
          y - fontSize
        );

        // move DOWNWARD
        drops[i] += speeds[i];

        // recycle when off the bottom
        if (drops[i] * fontSize > h + fontSize && Math.random() > 0.975) {
          drops[i] = -Math.random() * 20;
        }
      }

      animationId = requestAnimationFrame(draw);
    };

    setup();
    draw();

    const onResize = () => setup();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        opacity: 0.38,
      }}
    />
  );
}
